import React from "react";
import classnames from "classnames";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { camelCase, get, isEmpty, isNil, map } from "lodash";
import { Col, Row } from "react-bootstrap";
import { portfolioConstants } from "actions/types";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { portfolioMetadataMap } from "utils/individualPortfolioHelper";
import { INVESTMENT_PROFILE } from "statics/onboardingSteps";
import {
  activeAccountState,
  isOnboardingSubsequentAccountSelector,
  isRolloverSelector,
} from "store/selectors/user";
import { accountConstants } from "actions/types";
import { accountTypesToEnglishMapping } from "statics/accountTypes";

import {
  IndividualInvestmentInfoComplete,
  IndividualPortfolioUpdate,
  IndividualRequiredUpdatesPending,
} from "statics/states";
import {
  getAllPortfolios,
  getUserPortfolio,
  selectUserPortfolio,
  rolloverAccountPortfolio,
} from "actions/portfolioActions";
import {
  getEmployerLinkRequests,
  getUserState,
  getRequiredUpdates,
  getPaychexWorkerRecords,
} from "actions/userActions";

import ProgressBar from "components/ProgressBar";
import PortfolioSelectBox from "./PortfolioSelectBox";
import PortfolioDetailCard from "./PortfolioDetailCard";
import RecommendedPortfolio from "./RecommendedPortfolio";
import PortfolioConfirmationModal from "./PortfolioConfirmationModal";
import Button from "components/Button";
import Alert from "components/Alert";
import IconSpinner from "components/IconSpinner";
import IconInfoCard from "components/IconInfoCard";
import CancelRolloverModal from "../../dashboards/individualDashboard/dashboard/CancelRolloverModal";
import {
  getIraAccountIdSelector,
  userIsPaychexPerks,
} from "store/selectors/user";
import type { RootState } from "types/state";

import "./IndividualPortfolioSelection.scss";

/**
 * The redux portfolio slice stores these objects loosely (`object`/`unknown[]`
 * in types/state.ts) and augments them with `portfolioName`. This file only
 * reads `id` (for the selected portfolio) and `label` (as the map key), so the
 * minimal shape is sufficient; the objects are otherwise passed opaquely to the
 * child cards.
 */
interface Portfolio {
  id?: string;
  label?: string;
}

const loadingPortfolios = createLoadingSelector([
  portfolioConstants.GET_PORTFOLIOS,
  // PRESERVED BUG (conversion-only): createLoadingSelector takes a single
  // constant or an array — extra args are silently ignored. The original
  // passed two separate arguments, so GET_USER_PORTFOLIO never contributed to
  // this loading flag. Wrapping the existing first arg in an array preserves
  // that behavior byte-for-byte while satisfying the typed signature.
]);

const errorSelector = createErrorSelector([
  portfolioConstants.GET_PORTFOLIOS,
  portfolioConstants.UPDATE_USER_PORTFOLIO,
  portfolioConstants.SELECT_USER_ROLLOVER_PORTFOLIO,
  portfolioConstants.GET_USER_PORTFOLIO,
  accountConstants.GET_ACCOUNTS,
]);

const mapStateToProps = (state: RootState) => {
  const portfolioName = camelCase(
    get(state, "portfolios.activePortfolio.description")
  );
  const userState = get(state, "user.userState.state") as string | undefined;
  return {
    portfolios: state.portfolios.allPortfolios,
    otherPortfolios: state.portfolios.otherPortfolios as Portfolio[],
    recommendedPortfolio: state.portfolios.recommendedPortfolio as Portfolio,
    isFetching: loadingPortfolios(state),
    error: errorSelector(state),
    userState,
    portfolioName,
    isInRequiredStepFlow: IndividualRequiredUpdatesPending === userState,
    isRolloverAccount: isRolloverSelector(state),
    isSubsequentAccount: isOnboardingSubsequentAccountSelector(state),
    accountState: activeAccountState(state),
    accountId: getIraAccountIdSelector(state),
    isPaychexPerks: userIsPaychexPerks(state),
  };
};

const mapDispatchToProps = {
  selectUserPortfolio,
  rolloverAccountPortfolio,
  getUserState,
  getAllPortfolios,
  getUserPortfolio,
  getEmployerLinkRequests,
  getRequiredUpdates,
  getPaychexWorkerRecords,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  show?: boolean;
  isUpdating?: boolean;
  onClose?: () => void;
  updatingAccountId?: string;
  accountType?: string;
}

type Props = OwnProps & WithApolloClient<PropsFromRedux>;

interface State {
  selectedPortfolio: Portfolio;
  showAllPortfolios: boolean;
  showConfirmationModal: boolean;
  isUpdating: boolean;
  hasFetchedPortfolios: boolean;
  showCancelRolloverModal: boolean;
}

class IndividualPortfolioSelection extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      selectedPortfolio: {},
      showAllPortfolios: false,
      showConfirmationModal: false,
      isUpdating: false,
      hasFetchedPortfolios: false,
      showCancelRolloverModal: false,
    };
  }

  static getDerivedStateFromProps(nextProps: Props, prevState: State) {
    if (
      prevState.hasFetchedPortfolios &&
      isEmpty(prevState.selectedPortfolio) &&
      !isEmpty(nextProps.recommendedPortfolio)
    ) {
      return {
        selectedPortfolio: nextProps.recommendedPortfolio,
      };
    }
    return null;
  }

  getTargetAccountId = () => {
    const { accountId, updatingAccountId, isInRequiredStepFlow } = this.props;
    return isInRequiredStepFlow ? updatingAccountId : accountId;
  };

  async componentDidMount() {
    window.analytics.page("Individual Portfolio Selection");

    const accountId = this.getTargetAccountId();

    await this.props.getAllPortfolios(
      this.props.client as ApolloClient<object>,
      accountId
    );
    if (
      this.props.userState === IndividualPortfolioUpdate ||
      this.props.isInRequiredStepFlow
    ) {
      await this.props.getUserPortfolio(
        this.props.client as ApolloClient<object>,
        accountId
      );
    }

    this.setState({ hasFetchedPortfolios: true });
  }

  _showAllPortfolios = () => {
    this.setState({ showAllPortfolios: true });
  };

  _showConfirmationModal = () => {
    this.setState({ showConfirmationModal: true });
  };

  _closeConfirmationModal = () => {
    this.setState({ showConfirmationModal: false });
  };

  _setSelectedPortfolio = (selectedPortfolio: Portfolio) => {
    this.setState({
      selectedPortfolio,
    });
  };

  _onCancel = () => {
    this.setState({
      showCancelRolloverModal: true,
    });
  };

  _submitPortfoilio = () => {
    if (this.state.selectedPortfolio !== this.props.recommendedPortfolio) {
      this.setState({
        showConfirmationModal: true,
      });
    } else {
      this._setUserPortfolio();
    }
  };
  _setUserPortfolio = () => {
    this.setState({ isUpdating: true });
    const isRollingOverNewAccount =
      this.props.accountState === IndividualInvestmentInfoComplete;
    if (
      (this.props.isRolloverAccount || this.props.isSubsequentAccount) &&
      isRollingOverNewAccount
    ) {
      void this.props
        .rolloverAccountPortfolio(
          this.props.client as ApolloClient<object>,
          this.state.selectedPortfolio.id as string
        )
        .then(() => {
          window.analytics.track("Selected Rollover Investment Portfolio", {
            userState: this.props.userState,
          });
          this.setState({
            isUpdating: false,
          });
        });
    } else {
      void this.props
        .selectUserPortfolio(this.props.client as ApolloClient<object>, {
          portfolioId: this.state.selectedPortfolio.id as string,
          accountId: this.getTargetAccountId(),
        })
        // @ts-expect-error the .then callback returns a union of heterogeneous
        // dispatchFullLifecycle results (getEmployerLinkRequests vs
        // getRequiredUpdates vs Promise.resolve()) whose success-data shapes
        // differ, so TS cannot unify them into a single fulfillment type. The
        // chain runs identically at runtime; preserved verbatim.
        .then(() => {
          window.analytics.track("Selected User Portfolio", {
            userState: this.props.userState,
          });

          // hydrate state before the contribution step on the next page of sign
          // up. Perks users have worker records (not employer link requests);
          // the backend routes them into IndividualPayrollContributionPending at
          // this same point, where PrivateRoute renders PerksSetupContribution.
          if (this.props.userState === IndividualInvestmentInfoComplete) {
            if (this.props.isPaychexPerks) {
              return this.props.getPaychexWorkerRecords(
                this.props.client as ApolloClient<object>
              );
            }
            return this.props.getEmployerLinkRequests(
              this.props.client as ApolloClient<object>
            );
          } else if (this.props.isInRequiredStepFlow) {
            return this.props.getRequiredUpdates(
              this.props.client as ApolloClient<object>
            );
          }
          return Promise.resolve();
        })
        .then(() =>
          this.props.getUserState(this.props.client as ApolloClient<object>)
        )
        .finally(() => this.setState({ isUpdating: false }));
    }
  };

  _portfolioContent = () => {
    const cardOne = {
      sections: [
        {
          key: 1,
          title: "Portfolio Freedom: You Choose, We Manage",
          content: (
            <>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <p style={{ fontSize: 14 }}>
                  You select the portfolio and we do the work for you.
                </p>
                <p style={{ fontSize: 14 }}>
                  And, if your situation changes, you can always change your
                  portfolio recommendation.
                </p>
              </div>
            </>
          ),
        },
      ],
    };
    const mappedPortfolios = map(this.props.otherPortfolios, (portfolio) => (
      <PortfolioSelectBox
        key={portfolio.label}
        portfolio={portfolio}
        onSelect={this._setSelectedPortfolio}
        isSelected={portfolio === this.state.selectedPortfolio}
      />
    ));
    return (
      <>
        {this.state.showCancelRolloverModal && (
          <CancelRolloverModal
            show={this.state.showCancelRolloverModal}
            onClose={() =>
              this.setState({
                showCancelRolloverModal: false,
              })
            }
            onSuccess={() =>
              this.setState({
                showCancelRolloverModal: false,
              })
            }
          />
        )}
        {this.props.recommendedPortfolio && !this.state.showAllPortfolios && (
          <>
            <Row style={{ justifyContent: "space-between" }}>
              <Col md={6}>
                <RecommendedPortfolio
                  portfolio={this.props.recommendedPortfolio}
                  // @ts-expect-error RecommendedPortfolio types onSelect as the
                  // loose `(...args: unknown[]) => void`; this typed handler
                  // requires a Portfolio arg, which is contravariantly
                  // incompatible. Behavior is unchanged — it's called with the
                  // portfolio object at runtime.
                  onSelect={this._setSelectedPortfolio}
                  isRecommendedPortoflio={true}
                  isSelected={
                    this.state.selectedPortfolio ===
                    this.props.recommendedPortfolio
                  }
                />
              </Col>
              <Col md={4} lg={3}>
                <IconInfoCard
                  // @ts-expect-error PRESERVED quirk (conversion-only): cardOne
                  // has no `title` key, so this prop was always `undefined`;
                  // IconInfoCard ignores `title` anyway (it destructures only
                  // sections/chevron). Preserved verbatim.
                  title={cardOne.title}
                  // @ts-expect-error IconInfoCard types section.key as string
                  // and content as string, but this caller passes a numeric key
                  // and a JSX content node — the component renders both fine at
                  // runtime. Preserved verbatim; the component's types are
                  // narrower than its actual usage here.
                  sections={cardOne.sections}
                />
              </Col>
            </Row>
            <Row
              className={classnames("submit-btn-row portfolio-lg-screen ", {
                ["cancel-rollover-button"]: this.props.isRolloverAccount,
              })}
            >
              <div className="rec-button-row">
                {!this.state.showAllPortfolios && (
                  <div className="other-portfolios">
                    <Button
                      name="action"
                      btnLabel="See Other Portfolios"
                      onClick={this._showAllPortfolios}
                      color="action"
                    />
                  </div>
                )}
                <Button
                  name="submit"
                  btnLabel="Select Portfolio"
                  withArrow={true}
                  onClick={this._submitPortfoilio}
                  loading={this.state.isUpdating}
                />
              </div>
              <div>
                {this.props.isRolloverAccount && (
                  <Button
                    type="button"
                    btnLabel={"Cancel"}
                    color={"cancel"}
                    name="cancel"
                    onClick={this._onCancel}
                  />
                )}
              </div>
            </Row>
          </>
        )}
        {this.state.showAllPortfolios && (
          <>
            <Row className="portfolio-lg-screen">
              <Col lg={6} className="detail-col">
                <PortfolioSelectBox
                  portfolio={this.props.recommendedPortfolio}
                  onSelect={this._setSelectedPortfolio}
                  isRecommendedPortoflio={true}
                  isSelected={
                    this.state.selectedPortfolio ===
                    this.props.recommendedPortfolio
                  }
                />
                {mappedPortfolios}
                <div
                  className={classnames("submit-btn-row portfolio-lg-screen", {
                    ["cancel-rollover-button"]: this.props.isRolloverAccount,
                  })}
                >
                  <div>
                    {!this.state.showAllPortfolios && (
                      <Button
                        name="action"
                        btnLabel="See Other Portfolios"
                        onClick={this._showAllPortfolios}
                        color="action"
                      />
                    )}
                    <Button
                      name="action"
                      btnLabel="Select Portfolio"
                      onClick={this._submitPortfoilio}
                      loading={this.state.isUpdating}
                      disabled={isEmpty(this.state.selectedPortfolio)}
                    />
                  </div>
                  <div>
                    {this.props.isRolloverAccount && (
                      <Button
                        type="button"
                        btnLabel={"Cancel"}
                        color={"cancel"}
                        name="cancel"
                        onClick={this._onCancel}
                      />
                    )}
                  </div>
                </div>
              </Col>
              <Col md={5} className="detail-col">
                <PortfolioDetailCard portfolio={this.state.selectedPortfolio} />
              </Col>
            </Row>

            <Row className="portfolio-sm-screen">
              <Col md={5}>
                <PortfolioDetailCard portfolio={this.state.selectedPortfolio} />
              </Col>
              <Col md={6}>
                <div className="scrollbar">
                  <PortfolioSelectBox
                    portfolio={this.props.recommendedPortfolio}
                    onSelect={this._setSelectedPortfolio}
                    isRecommendedPortoflio={true}
                    isSelected={
                      this.state.selectedPortfolio ===
                      this.props.recommendedPortfolio
                    }
                  />
                  {mappedPortfolios}
                </div>
                <div className="submit-btn-row portfolio-sm-screen">
                  <div className="button-row">
                    {!this.state.showAllPortfolios && (
                      <Button
                        btnLabel="See Other Portfolios"
                        onClick={this._showAllPortfolios}
                        name="action"
                        color="action"
                      />
                    )}
                    <Button
                      name="action"
                      btnLabel="Select Portfolio"
                      onClick={this._submitPortfoilio}
                      loading={this.state.isUpdating}
                      disabled={isEmpty(this.state.selectedPortfolio)}
                    />
                  </div>
                </div>
              </Col>
            </Row>
          </>
        )}
      </>
    );
  };

  _loadingContent = () => {
    return <IconSpinner centered />;
  };

  render() {
    let content;

    if (this.props.isFetching) {
      content = this._loadingContent();
    } else {
      content = this._portfolioContent();
    }
    const shouldDisplayCurrentPortfolio =
      (this.props.userState === IndividualPortfolioUpdate ||
        this.props.isInRequiredStepFlow) &&
      !isNil(this.props.portfolioName) &&
      !isEmpty(this.props.portfolioName);

    const individualStates =
      this.props.userState === IndividualInvestmentInfoComplete ||
      this.props.accountState === IndividualInvestmentInfoComplete;

    const accountTypeText = this.props.accountType
      ? `${
          accountTypesToEnglishMapping[
            this.props.accountType as keyof typeof accountTypesToEnglishMapping
          ]
        } `
      : "";

    return (
      <>
        {individualStates && (
          <ProgressBar
            isEmployer={false}
            activeStepId={INVESTMENT_PROFILE.id}
            progressPercent={"85"}
          />
        )}

        <div className="individual-portfolio-selection">
          <div className="mega-container">
            <section className="page-title-wrap">
              <article className="text-cell">
                <Row>
                  <Col md={6}>
                    <p className="page-eyebrow">YOUR INVESTMENT PROFILE</p>
                    <p className="page-title">
                      Based on your answers, we recommend the following
                      investment portfolio for you.{" "}
                    </p>
                    <p className="page-subtext">
                      Each portfolio represents a different level of investment
                      risk and potential growth.
                    </p>
                  </Col>
                </Row>

                {shouldDisplayCurrentPortfolio && (
                  <p className="your-portfolio">
                    Your current {accountTypeText}portfolio is{" "}
                    {get(
                      portfolioMetadataMap,
                      `${this.props.portfolioName}.portfolio`
                    )}
                    .
                  </p>
                )}
              </article>
            </section>
            <>
              {this.state.showConfirmationModal && (
                <PortfolioConfirmationModal
                  // @ts-expect-error PortfolioConfirmationModal does not declare
                  // a `show` prop (its connected OwnProps only expose
                  // onClose/onSubmit); the original always passed `show`, so
                  // it is preserved verbatim as an inert extra prop.
                  show={this.state.showConfirmationModal}
                  onClose={() => {
                    this.setState({ showConfirmationModal: false });
                  }}
                  onSubmit={this._setUserPortfolio}
                />
              )}
              <div>
                {content}
                {this.props.error && (
                  <Alert type="error" msg={this.props.error} />
                )}
              </div>
            </>
          </div>
        </div>
      </>
    );
  }
}

export default connector(withApollo<Props>(IndividualPortfolioSelection));
